import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle2, AlertCircle, XCircle, Clock, FileText, Upload } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { api } from '@/shared/api/axios';
import { useSampleMutations } from '../api';
import type { FeedbackOutcome, SampleRequest } from '../types';

interface SampleFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  sample: SampleRequest | null;
}

const OUTCOMES: Array<{
  id: FeedbackOutcome;
  title: string;
  desc: string;
  icon: typeof CheckCircle2;
  color: string;
  bg: string;
  border: string;
}> = [
  {
    id: 'approved',
    title: 'Approved',
    desc: 'Customer trialled & approved the oil. Ready for technical review or quotation.',
    icon: CheckCircle2,
    color: 'text-emerald-700',
    bg: 'bg-emerald-50/70',
    border: 'border-emerald-200',
  },
  {
    id: 'needs_changes',
    title: 'Needs Changes',
    desc: 'Customer wants a revised grade, viscosity tweak, or different pack variant.',
    icon: AlertCircle,
    color: 'text-amber-700',
    bg: 'bg-amber-50/70',
    border: 'border-amber-200',
  },
  {
    id: 'rejected',
    title: 'Rejected',
    desc: 'Sample failed customer requirements or test spec (Mandatory note required).',
    icon: XCircle,
    color: 'text-red-700',
    bg: 'bg-red-50/70',
    border: 'border-red-200',
  },
  {
    id: 'no_response',
    title: 'No Response',
    desc: 'Customer went quiet after trial window. Prevents silent pipeline stalling.',
    icon: Clock,
    color: 'text-slate-700',
    bg: 'bg-slate-50/70',
    border: 'border-slate-200',
  },
];

export function SampleFeedbackDialog({ open, onClose, sample }: SampleFeedbackDialogProps) {
  const { recordFeedback } = useSampleMutations();

  const [outcome, setOutcome] = useState<FeedbackOutcome>('approved');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  if (!sample) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (outcome === 'rejected' && !notes.trim()) {
      toast.error('Please add a note explaining why the sample was rejected.');
      return;
    }

    recordFeedback.mutate(
      {
        id: sample._id,
        payload: {
          outcome,
          notes: notes.trim(),
          attachments,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Feedback recorded for ${sample.code}.`);
          onClose();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.error || 'Failed to record feedback.');
        },
      },
    );
  };

  const isSubmitting = recordFeedback.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record Customer Verdict: ${sample.code}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-xs text-slate-600 mb-2">
          Customer: <span className="font-bold text-slate-900">{sample.customerName}</span> ·{' '}
          {sample.lines?.map((l) => `${l.productName} (${l.packSize})`).join(', ')}
        </div>

        {/* Outcome Cards */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2">
            Trial Verdict <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {OUTCOMES.map((item) => {
              const Icon = item.icon;
              const isSelected = outcome === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setOutcome(item.id)}
                  className={`p-3 rounded-xl border text-left transition-all flex items-start gap-2.5 ${
                    isSelected
                      ? `${item.border} ${item.bg} ring-2 ring-blue-500`
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 ${item.color}`} />
                  <div>
                    <div className="text-xs font-bold text-slate-900">{item.title}</div>
                    <div className="text-data-xs text-slate-500 mt-0.5 leading-tight">{item.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>



        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-slate-500" />
            Customer Trial Notes & Observations{' '}
            {outcome === 'rejected' && <span className="text-red-500">* (Mandatory for rejection)</span>}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              outcome === 'rejected'
                ? 'Specify why the customer rejected the sample (e.g., failed 4-ball wear test or viscosity drop)...'
                : 'Customer test remarks, machine runtime test results, or formulation feedback...'
            }
            rows={3}
            className="w-full p-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
            required={outcome === 'rejected'}
          />
        </div>

        {/* Trial Run Result Upload (Item 39) */}
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3">
          <label className="block text-xs font-semibold text-slate-800 mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Upload className="w-3.5 h-3.5 text-indigo-600" />
              Upload Trial Run Results / Lab Test Report
            </span>
            {isUploading && <span className="text-data-xs text-indigo-600 font-medium">Uploading...</span>}
          </label>
          <p className="text-data-xs text-slate-500 mb-2">
            Attach trial test certificate, customer observation sheet, or machine performance report (PDF, Excel, Images).
          </p>

          <input
            type="file"
            id="trial-run-result-input"
            className="hidden"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              setIsUploading(true);
              try {
                const uploadedUrls: string[] = [];
                for (const f of files) {
                  const fd = new FormData();
                  fd.append('file', f);
                  const { data } = await api.post<{ url: string }>('/api/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  });
                  if (data?.url) uploadedUrls.push(data.url);
                }
                setAttachments((prev) => [...prev, ...uploadedUrls]);
                toast.success(`${files.length} document(s) attached.`);
              } catch {
                toast.error('Failed to upload trial document.');
              } finally {
                setIsUploading(false);
                e.target.value = '';
              }
            }}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isUploading}
              onClick={() => document.getElementById('trial-run-result-input')?.click()}
              className="text-xs gap-1.5 h-8 bg-white border-slate-200"
            >
              <Upload className="w-3.5 h-3.5 text-slate-600" />
              Choose File(s)
            </Button>
            {attachments.map((url, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-200 px-2 py-1 text-data-xs font-medium text-indigo-800"
              >
                <span>Report #{idx + 1}</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline ml-0.5 hover:text-indigo-950"
                >
                  View
                </a>
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                  className="ml-1 text-slate-400 hover:text-red-500 font-bold"
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="gap-2">
            {isSubmitting ? (
              <>
                <Spinner className="h-4 w-4" /> Recording...
              </>
            ) : (
              'Save Feedback'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
